/**
 * H5 会话换票。
 *
 * App 打开本页时 URL 只带 patientId、不带登录凭证，后端此前只能信任请求参数里的
 * patientId，导致写接口可被冒充。现在改为：首次带上 patientId 的请求发出前，先用
 * 该 id 换一张服务端签发的短期会话令牌，之后所有请求通过 x-h5-session 头带上，
 * 后端的写接口一律以会话为准、忽略请求参数里的身份。
 *
 * 换票对业务代码透明：不需要改任何页面，由 ApolloClient 的链路统一处理。
 */
import axios from 'axios'

export const SESSION_HEADER = 'x-h5-session'

// 请求变量里代表「当前用户」的字段名（不同接口叫法不同）
const ID_KEYS = ['patientId', 'userId', 'selfId']

// 已登录会话存 localStorage：WebView 已开启 DOM storage，按域名隔离、App 重启仍在。
// 存储不可用（无痕、被清理、配额满）时退回内存，仅当次页面有效，不影响阅读
const STORAGE_KEY = 'h5_verified_session'
// 未登录会话也持久化：每次打开文章都重新换票会迅速耗尽服务端的换票限额，
// 表现为整页请求失败。复用未过期的会话，一个有效期内只换一次
const ANON_STORAGE_KEY = 'h5_anon_session'

const storage = {
  read(key) {
    try {
      return window.localStorage.getItem(key)
    } catch (e) {
      return null
    }
  },
  write(key, value) {
    try {
      if (value) window.localStorage.setItem(key, value)
      else window.localStorage.removeItem(key)
    } catch (e) {
      // 忽略：内存里仍持有，当次页面可用
    }
  },
}

// 会话令牌形如 base64url(payload).签名，payload 里有 pid 与 exp
const readPayload = (value) => {
  try {
    const body = String(value).split('.')[0]
    return JSON.parse(atob(body.replace(/-/g, '+').replace(/_/g, '/')))
  } catch (e) {
    return null
  }
}

// 还剩至少 5 分钟才算可用，避免临界过期
export const usableFor = (value, patientId) => {
  const payload = readPayload(value)
  if (!payload || !payload.exp) return false
  if (payload.exp - Date.now() < 5 * 60 * 1000) return false
  return !patientId || String(payload.pid) === String(patientId)
}

let pending = null
let issuedFor = null
let verifiedToken = storage.read(STORAGE_KEY)
let token = storage.read(ANON_STORAGE_KEY)

export const setVerifiedToken = (value) => {
  verifiedToken = value || null
  storage.write(STORAGE_KEY, verifiedToken)
}

// 已登录会话过期（或即将过期）就清掉，退回未登录会话。
// 不清的话，过期的已登录会话会一直被优先带上，服务端视为无会话，整页请求失败、一直加载中；
// 而它存在 localStorage 里，重开页面也不会自愈（登录约 2 小时后必现）
const dropExpiredVerified = () => {
  if (verifiedToken && !usableFor(verifiedToken)) setVerifiedToken(null)
}

// 已登录会话优先：写接口据它放行
export const getSessionToken = () => {
  dropExpiredVerified()
  return verifiedToken || token
}

export const getVerifiedToken = () => {
  dropExpiredVerified()
  return verifiedToken
}

/**
 * 服务端不认手里的会话时（返回 requires h5 session）全部丢弃，下次请求重新换票。
 * 本地按令牌里的到期时间判断有效期，手机时间不准时可能与服务端不一致，需要这条兜底
 */
export const forgetSessions = () => {
  token = null
  pending = null
  issuedFor = null
  storage.write(ANON_STORAGE_KEY, null)
  setVerifiedToken(null)
}

export const SESSION_REQUIRED_MESSAGE = 'requires h5 session'

export const isSessionRejected = (resultOrError) => {
  if (!resultOrError) return false
  const lists = [resultOrError.errors, resultOrError.graphQLErrors, resultOrError.result && resultOrError.result.errors]
  return lists.some(
    (list) => Array.isArray(list) && list.some((e) => String((e && e.message) || '').indexOf(SESSION_REQUIRED_MESSAGE) >= 0)
  )
}

export const clearVerifiedToken = () => setVerifiedToken(null)

const usable = (value) =>
  // 后端下发的他人 id 是 anon_ 开头的令牌，不能拿去换票
  typeof value === 'string' && value && !value.startsWith('anon_') ? value : ''

const OBJECT_ID = /^[0-9a-f]{24}$/i

/**
 * 从地址里取当前患者 id：App 打开的地址形如
 * /knowledge/:systemType/:knowledgeId/:patientId(/:fontSize)，也可能带 ?patientId=。
 * 患者 id 是 24 位十六进制，而文章 id 是短随机串、字号是数字，不会误取。
 */
export const patientIdFromLocation = (loc) => {
  const location = loc || (typeof window !== 'undefined' && window.location)
  if (!location) return ''
  const search = String(location.search || '')
  const matched = search.match(/[?&]patientId=([^&]+)/)
  if (matched) return usable(decodeURIComponent(matched[1]))
  const segments = String(location.pathname || '').split('/').filter(Boolean)
  const found = segments.find((seg) => OBJECT_ID.test(seg))
  return usable(found)
}

/**
 * 取本次请求应使用的患者 id：优先用请求变量里的，没有则回退到地址。
 * 回退是必须的——页面上存在不带 patientId 的请求（如仅按文章 id 查询），
 * 若因此换不到票，在后端开启强制后会整页打不开。
 */
export const pickPatientId = (variables, loc) => {
  if (variables) {
    for (const key of ID_KEYS) {
      const value = usable(variables[key])
      if (value) return value
    }
  }
  return patientIdFromLocation(loc)
}

const MUTATION = `mutation CreateH5Session($patientId: ID!) {
  createH5Session(patientId: $patientId) { token verified }
}`

/**
 * 确保已持有会话；同一 patientId 只换一次，并发调用共用同一个请求。
 * 换票失败不阻断页面——后端在灰度期仍接受无会话的请求。
 */
// App 注入凭证时使用的名字，三处保持一致
export const APP_TOKEN_GLOBAL = '__IHEALTH_APP_TOKEN__'
export const APP_TOKEN_STORAGE_KEY = 'ihealth_app_token'

/**
 * 取 App 传进来的 pigeon 凭证，按稳妥程度排序：
 *
 * 1) WebView 注入的全局变量（推荐）——不进地址栏，不会出现在截图或 Referer 里
 * 2) WebView 注入的 localStorage——同上，且页面刷新后仍在
 * 3) 地址栏 ?token= / ?jwt=（兜底）——实现最简单，但凭证会随地址外泄
 *
 * 取不到就退回短信登录，老版本 App 行为不变。
 */
export const appToken = (loc) => {
  if (typeof window !== 'undefined') {
    const injected = window[APP_TOKEN_GLOBAL]
    if (typeof injected === 'string' && injected) return injected
    const stored = storage.read(APP_TOKEN_STORAGE_KEY)
    if (stored) return stored
  }
  const location = loc || (typeof window !== 'undefined' && window.location)
  if (!location) return ''
  const m = String(location.search || '').match(/[?&](?:token|jwt)=([^&]+)/)
  if (!m) return ''
  try {
    return decodeURIComponent(m[1])
  } catch (e) {
    return m[1]
  }
}

// 兼容旧名
export const appTokenFromLocation = appToken

export const ensureSession = (uri, patientId) => {
  if (!patientId) return Promise.resolve(null)
  dropExpiredVerified()
  // 已登录会话优先，无需再换票
  if (verifiedToken) return Promise.resolve(verifiedToken)
  // App 带了凭证却还没换到已登录会话时，不要复用手里的未登录会话——
  // 否则本该免登录的用户仍会被弹框（比如凭证是页面加载后才注入进来的）
  const credential = appToken()
  // 复用本地未过期的未登录会话，避免每次打开文章都换票
  if (!credential && token && usableFor(token, patientId)) {
    issuedFor = patientId
    return Promise.resolve(token)
  }
  if (pending && issuedFor === patientId) return pending

  issuedFor = patientId
  // App 传来的 pigeon 凭证随换票一并送出，直接换到已登录会话
  const headers = credential ? { Authorization: 'Bearer ' + credential } : undefined
  pending = axios
    .post(uri, { query: MUTATION, variables: { patientId } }, { timeout: 8000, headers })
    .then((res) => {
      const next = res && res.data && res.data.data && res.data.data.createH5Session
      token = (next && next.token) || null
      // App 带凭证换来的是已登录会话，直接落到已登录位，互动无需再弹框
      if (next && next.verified && token) {
        setVerifiedToken(token)
      } else {
        storage.write(ANON_STORAGE_KEY, token)
      }
      return token
    })
    .catch(() => {
      token = null
      return null
    })
    .then((result) => {
      pending = null
      return result
    })

  return pending
}

const LOGIN_CODE_MUTATION = `mutation SendH5LoginCode($mobile: String!) {
  sendH5LoginCode(mobile: $mobile)
}`

const LOGIN_MUTATION = `mutation LoginH5Session($mobile: String!, $verificationCode: String!) {
  loginH5Session(mobile: $mobile, verificationCode: $verificationCode) {
    token
    patientId
    verified
  }
}`

const callGraphql = async (uri, query, variables) => {
  let res
  try {
    res = await axios.post(uri, { query, variables }, { timeout: 15000 })
  } catch (e) {
    // 非 2xx 时业务错误在响应体里；取不到就是网络问题。
    // 不能把 axios 的 "Request failed with status code 400" 原样显示给用户
    res = e && e.response
    if (!res || !res.data || !res.data.errors) throw new Error('网络不太稳定，请稍后再试')
  }
  const errors = res && res.data && res.data.errors
  if (errors && errors.length) {
    const err = new Error(errors[0].message || '请求失败')
    err.code = (errors[0].extensions || {}).code
    throw err
  }
  return res.data.data
}

export const sendLoginCode = (uri, mobile) =>
  callGraphql(uri, LOGIN_CODE_MUTATION, { mobile })

// 登录成功后保存已验证会话，后续写操作即可放行
export const login = async (uri, mobile, verificationCode) => {
  const data = await callGraphql(uri, LOGIN_MUTATION, { mobile, verificationCode })
  const result = data && data.loginH5Session
  if (!result || !result.token) throw new Error('登录失败')
  setVerifiedToken(result.token)
  return result
}

// 后端返回该错误码时表示需要登录
export const LOGIN_REQUIRED_CODE = 'H5_LOGIN_REQUIRED'

/**
 * 判断是否「需要登录」。
 *
 * 后端对未登录的写操作返回 HTTP 400，apollo-link-http 会把非 2xx 当成网络错误抛出，
 * 错误消息此时在 error.result.errors 下，而不是 graphQLErrors ——
 * 只看后者会漏判，表现为点赞/评论既不弹登录框也没反应。
 */
export const isLoginRequiredError = (error) => {
  if (!error) return false
  const lists = [error.graphQLErrors, error.errors, error.result && error.result.errors]
  for (const list of lists) {
    if (Array.isArray(list) && list.some((e) => String((e && e.message) || '').indexOf(LOGIN_REQUIRED_CODE) >= 0)) {
      return true
    }
  }
  return String(error.message || '').indexOf(LOGIN_REQUIRED_CODE) >= 0
}

// 仅供测试使用
export const __reset = () => {
  token = null
  pending = null
  issuedFor = null
  verifiedToken = null
  storage.write(STORAGE_KEY, null)
  storage.write(ANON_STORAGE_KEY, null)
}



// WEBPACK FOOTER //
// ./src/common/h5Session.js